import * as XLSX from 'xlsx'

const FEE_COLUMN_CANDIDATES = ['商业物管费', '水电公摊费', '住户物管费', '物业管理费', '物业费', '水费公摊费', '电梯维保费']
const SKIP_COLUMNS = new Set(['序号', '物业名称', '建筑面积', '累欠月数', '负责人', '历史', '本月', '合计', '倍率系数', '接房状态', '装修状态', '入住状态'])

/**
 * 将慧景类「欠收款分户分项汇总表」宽表转为系统标准长表。
 * 非宽表则原样返回文件。
 */
export async function adaptOweFeeImportFile(file) {
  const buffer = await readAsArrayBuffer(file)
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true })
  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false })
  if (!matrix || matrix.length < 2) return file

  const headerInfo = findHeaderRow(matrix)
  if (!headerInfo) return file

  const { headerRowIndex, headers } = headerInfo
  if (!isWideOweFeeSheet(headers)) return file

  const floorIdx = headers.indexOf('大楼名称')
  const roomIdx = headers.indexOf('房屋编号')
  const ownerIdx = headers.indexOf('客户名称')
  const telIdx = headers.indexOf('联系电话')
  const monthIdx = headers.indexOf('计费月份')
  const feeIndexes = headers
    .map((name, index) => ({ name: String(name || '').trim(), index }))
    .filter(item => item.name && !SKIP_COLUMNS.has(item.name) && isFeeAmountColumn(item.name, headers, item.index))

  if (!feeIndexes.length) {
    throw new Error('识别到宽表格式，但未找到可导入的收费项金额列')
  }

  const longRows = [['房屋信息', '业主名称', '手机号', '收费项目', '收费项目ID', '欠费金额', '开始时间', '结束时间', '备注']]
  for (let r = headerRowIndex + 1; r < matrix.length; r++) {
    const row = matrix[r] || []
    const floorName = String(row[floorIdx] || '').trim()
    const roomKey = String(row[roomIdx] || '').trim()
    if (!floorName || !roomKey) continue

    const roomName = buildRoomName(floorName, roomKey)
    if (!roomName) continue
    const ownerName = ownerIdx >= 0 ? String(row[ownerIdx] || '').trim() : ''
    const ownerTel = telIdx >= 0 ? firstPhone(String(row[telIdx] || '')) : ''
    const billingMonth = monthIdx >= 0 ? String(row[monthIdx] || '').trim() : ''
    const { startTime, endTime } = billingMonthToRange(billingMonth)

    feeIndexes.forEach(feeCol => {
      const amount = parseAmount(row[feeCol.index])
      if (amount === null || amount <= 0) return
      longRows.push([
        roomName,
        ownerName,
        ownerTel,
        feeCol.name,
        // 同步放入收费项目列名，便于值映射到系统 configId
        feeCol.name,
        amount,
        startTime,
        endTime,
        ''
      ])
    })
  }

  if (longRows.length <= 1) {
    throw new Error('宽表转换后没有金额大于 0 的欠费明细，请检查文件')
  }

  const outSheet = XLSX.utils.aoa_to_sheet(longRows)
  const outBook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(outBook, outSheet, '欠费数据')
  const outBuffer = XLSX.write(outBook, { type: 'array', bookType: 'xlsx' })
  const baseName = String(file.name || '欠费明细').replace(/\.(xls|xlsx)$/i, '')
  return new File([outBuffer], `${baseName}-标准长表.xlsx`, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

function isWideOweFeeSheet(headers) {
  const set = new Set(headers.map(h => String(h || '').trim()))
  const hasRoom = set.has('大楼名称') && set.has('房屋编号')
  const hasMonth = set.has('计费月份')
  const hasFeeCol = FEE_COLUMN_CANDIDATES.some(name => set.has(name)) ||
    headers.some((h, idx) => isFeeAmountColumn(String(h || '').trim(), headers, idx))
  return hasRoom && hasMonth && hasFeeCol
}

function isFeeAmountColumn(name, headers, index) {
  if (!name || SKIP_COLUMNS.has(name)) return false
  if (FEE_COLUMN_CANDIDATES.includes(name)) return true
  if (name.endsWith('费') && name !== '倍率系数') return true
  // 排除紧跟在金额列后的倍率系数
  if (name === '倍率系数') return false
  if (headers[index + 1] === '倍率系数' && name.includes('费')) return true
  return false
}

function findHeaderRow(matrix) {
  const limit = Math.min(matrix.length, 20)
  for (let i = 0; i < limit; i++) {
    const headers = (matrix[i] || []).map(cell => String(cell || '').trim())
    if (headers.includes('房屋编号') && (headers.includes('大楼名称') || headers.includes('收费对象') || headers.includes('房屋信息'))) {
      return { headerRowIndex: i, headers }
    }
    if ((headers.includes('期栋') || headers.includes('楼栋编号')) && headers.includes('单元') && headers.includes('房号') && headers.includes('欠费金额')) {
      return null // 三列旧长表，由导入组件直接解析
    }
    if ((headers.includes('收费对象') || headers.includes('房屋信息')) && headers.includes('收费项目') && headers.includes('欠费金额')) {
      return null // 已是标准长表
    }
  }
  return null
}

function buildRoomName(floorName, roomKey) {
  const floorNum = String(floorName).replace(/栋$/g, '').trim()
  const key = String(roomKey).trim()
  if (!floorNum || !key) return ''
  if (key.split('-').length >= 2) {
    return `${floorNum}-${key}`
  }
  return `${floorNum}-1-${key}`
}

function billingMonthToRange(billingMonth) {
  const text = String(billingMonth || '').trim()
  if (/^\d{6}$/.test(text)) {
    const year = Number(text.slice(0, 4))
    const month = Number(text.slice(4, 6))
    const startTime = `${year}-${pad(month)}-01`
    const endDay = new Date(year, month, 0).getDate()
    const endTime = `${year}-${pad(month)}-${pad(endDay)}`
    return { startTime, endTime }
  }
  return { startTime: '', endTime: '' }
}

function parseAmount(value) {
  if (value === null || value === undefined || value === '') return null
  let text = String(value).trim()
  // SheetJS raw:false 可能读成 "$1,351.90" / "￥200.00" / "-"
  if (!text || text === '-' || text === '—' || text === '--') return null
  text = text
    .replace(/[￥¥$€]/g, '')
    .replace(/元/g, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '')
    .trim()
  if (!text) return null
  const num = Number(text)
  if (!Number.isFinite(num)) return null
  return Number(num.toFixed(2))
}

function firstPhone(text) {
  const matched = String(text || '').match(/1\d{10}/)
  return matched ? matched[0] : String(text || '').trim().split(/\s+/)[0] || ''
}

function pad(n) {
  return String(n).padStart(2, '0')
}

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = () => reject(new Error('无法读取所选文件'))
    reader.readAsArrayBuffer(file)
  })
}
